import Phaser from 'phaser'
import { UPGRADES, getUpgradeCost } from '../data/upgrades'
import * as GS from '../systems/GameState'
import { getPublicKey } from '../chain/wallet'
import { fetchPlayerState } from '../chain/player'
import { purchaseUpgrade, startRun, delegateRun, isRunStateDelegated, undelegateRun, waitForUndelegation, requestVrfSeed, waitForSeed, waitForDelegation } from '../chain/run'
import { isDevnet, getCluster, getConnection } from '../chain/connection'
import { cachePlayerState, cacheRunState } from '../systems/GameState'
import type { UpgradeId } from '../types/upgrades'

const GAME_W = 720

export default class HubScene extends Phaser.Scene {
  private busy = false
  private statusBg!: Phaser.GameObjects.Rectangle
  private statusText!: Phaser.GameObjects.Text

  constructor() {
    super('HubScene')
  }

  create(): void {
    this.cameras.main.setBackgroundColor(0x1a1a2e)
    this.busy = false

    const cam = this.cameras.main
    this.statusBg = this.add.rectangle(
      cam.centerX, cam.centerY, cam.width, cam.height, 0x000000, 0.7
    ).setDepth(100).setVisible(false)

    this.statusText = this.add.text(cam.centerX, cam.centerY, '', {
      fontSize: '28px', fontFamily: 'monospace', color: '#FFFFFF',
    }).setOrigin(0.5).setDepth(101).setVisible(false)

    this.drawUI()
  }

  private showStatus(msg: string): void {
    this.statusText.setText(msg).setColor('#FFFFFF')
    this.statusBg.setVisible(true)
    this.statusText.setVisible(true)
  }

  private hideStatus(): void {
    this.statusBg.setVisible(false)
    this.statusText.setVisible(false)
  }

  private async flashError(msg: string): Promise<void> {
    this.statusText.setText(msg).setColor('#FF4444')
    this.statusBg.setVisible(true)
    this.statusText.setVisible(true)
    await this.sleep(1500)
    this.hideStatus()
  }

  drawUI(): void {
    const wasVisible = this.statusBg?.visible ?? false
    const prevMsg = this.statusText?.text ?? ''
    const prevColor = this.statusText?.style?.color ?? '#FFFFFF'
    this.children.removeAll()

    const cam = this.cameras.main
    this.statusBg = this.add.rectangle(
      cam.centerX, cam.centerY, cam.width, cam.height, 0x000000, 0.7
    ).setDepth(100).setVisible(wasVisible)

    this.statusText = this.add.text(cam.centerX, cam.centerY, prevMsg, {
      fontSize: '28px', fontFamily: 'monospace', color: prevColor,
    }).setOrigin(0.5).setDepth(101).setVisible(wasVisible)

    // Devnet banner
    if (getCluster() === 'devnet') {
      this.add.rectangle(GAME_W / 2, 1264, GAME_W, 32, 0xcc0000).setDepth(999)
      this.add.text(GAME_W / 2, 1264, 'DEVNET', {
        fontSize: '18px', fontFamily: 'monospace', color: '#FFFFFF', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(1000)
    }

    const cash = GS.getCash()
    const centerX = GAME_W / 2

    let y = 60

    // --- Title ---
    this.add.text(centerX, y, 'DRILL IDLE', {
      fontSize: '44px', fontFamily: 'monospace', color: '#FFD700', fontStyle: 'bold',
    }).setOrigin(0.5)

    y += 80

    // --- Cash ---
    this.add.text(centerX, y, `Cash: $${cash}`, {
      fontSize: '36px', fontFamily: 'monospace', color: '#00FF00',
    }).setOrigin(0.5)

    y += 100

    // --- Column Select ---
    this.add.text(centerX, y, '-- Select Column --', {
      fontSize: '28px', fontFamily: 'monospace', color: '#AAAAAA',
    }).setOrigin(0.5)
    y += 56

    const btnWidth = 104
    const btnGap = 20
    const totalWidth = 5 * btnWidth + 4 * btnGap
    const startX = (GAME_W - totalWidth) / 2 + btnWidth / 2

    for (let i = 0; i < 5; i++) {
      const bx = startX + i * (btnWidth + btnGap)
      const btn = this.add.rectangle(bx, y, btnWidth, 80, 0x444466)
        .setInteractive({ useHandCursor: true })
        .setStrokeStyle(3, 0x8888AA)
      this.add.text(bx, y, `${i + 1}`, {
        fontSize: '36px', fontFamily: 'monospace', color: '#FFFFFF', fontStyle: 'bold',
      }).setOrigin(0.5)

      btn.on('pointerdown', () => this.handleStartDrill(i))
      btn.on('pointerover', () => btn.setFillStyle(0x6666AA))
      btn.on('pointerout', () => btn.setFillStyle(0x444466))
    }

    y += 90

    // --- Upgrades ---
    this.add.text(centerX, y, '-- Upgrades --', {
      fontSize: '28px', fontFamily: 'monospace', color: '#AAAAAA',
    }).setOrigin(0.5)
    y += 48

    const upgradeKeys: UpgradeId[] = ['drill', 'fuelTank', 'cargo', 'negotiator', 'luck']
    const upgradeIndexMap: Record<UpgradeId, number> = { drill: 0, fuelTank: 1, cargo: 2, negotiator: 3, luck: 4 }

    for (const key of upgradeKeys) {
      const upg = UPGRADES[key]
      const level = GS.getUpgradeLevel(key)
      const cost = getUpgradeCost(upg, level)
      const maxed = level >= upg.maxLevel

      const label = maxed
        ? `${upg.name} [Lv ${level}] MAX`
        : `${upg.name} [Lv ${level}] - $${cost}`

      const canAfford = !maxed && cash >= cost
      const btnColor = maxed ? 0x333333 : canAfford ? 0x2E5A2E : 0x5A2E2E

      const btn = this.add.rectangle(centerX, y + 24, 600, 64, btnColor)
        .setInteractive({ useHandCursor: !maxed && canAfford })
        .setStrokeStyle(2, 0x666666)

      this.add.text(centerX, y + 24, label, {
        fontSize: '24px', fontFamily: 'monospace',
        color: maxed ? '#666666' : canAfford ? '#AAFFAA' : '#FFAAAA',
      }).setOrigin(0.5)

      let desc = upg.description
      if (!maxed) {
        const nextLevel = upg.levels[level + 1]
        if (key === 'drill') desc += ` (${Math.round((nextLevel as { fuelMultiplier: number }).fuelMultiplier * 100)}% fuel)`
        else if (key === 'fuelTank') desc += ` (${(nextLevel as { capacity: number }).capacity} fuel)`
        else if (key === 'cargo') desc += ` (${(nextLevel as { capacity: number }).capacity} weight)`
        else if (key === 'negotiator') desc += ` (${(nextLevel as { priceMultiplier: number }).priceMultiplier}x price)`
        else if (key === 'luck') desc += ` (${(nextLevel as { rarityMultiplier: number }).rarityMultiplier}x luck)`
      }
      this.add.text(centerX, y + 70, desc, {
        fontSize: '20px', fontFamily: 'monospace', color: '#888888',
      }).setOrigin(0.5)

      if (!maxed && canAfford) {
        btn.on('pointerdown', () => this.handlePurchase(upgradeIndexMap[key]))
      }

      y += 104
    }

    // --- Stats Summary ---
    y += 10
    this.add.text(centerX, y, `Fuel: ${GS.getFuelCapacity()} | Cargo: ${GS.getCargoCapacity()} | Drill: ${Math.round(GS.getDrillFuelMultiplier() * 100)}%`, {
      fontSize: '22px', fontFamily: 'monospace', color: '#777777',
    }).setOrigin(0.5)
  }

  private async handlePurchase(upgradeIndex: number): Promise<void> {
    if (this.busy) return
    this.busy = true
    try {
      this.showStatus('Purchasing upgrade...')
      const sig = await purchaseUpgrade(upgradeIndex)
      // Wait for confirmation before re-fetching
      const conn = getConnection()
      const { blockhash, lastValidBlockHeight } = await conn.getLatestBlockhash()
      await conn.confirmTransaction({ signature: sig, blockhash, lastValidBlockHeight }, 'confirmed')
      const pubkey = getPublicKey()!
      const ps = await fetchPlayerState(pubkey)
      if (ps) cachePlayerState(ps)
      this.hideStatus()
      this.drawUI()
    } catch (err) {
      console.error('Purchase failed:', err)
      await this.flashError('Purchase failed')
    } finally {
      this.busy = false
    }
  }

  private async handleStartDrill(column: number): Promise<void> {
    if (this.busy) return
    this.busy = true
    try {
      const pubkey = getPublicKey()!
      if (!isDevnet()) {
        throw new Error('VRF requires devnet. Switch cluster to devnet to start a run.')
      }

      // If RunState is still delegated (e.g. from an interrupted session), undelegate first.
      const delegated = await isRunStateDelegated(pubkey)
      if (delegated) {
        this.showStatus('Preparing drill...')
        await undelegateRun()
        await waitForUndelegation(pubkey)
        await this.sleep(500)
      }

      // Send startRun tx
      this.showStatus('Starting drill...')
      await startRun(column)
      await this.sleep(500)

      // Delegate first, then request VRF seed on the ER
      this.showStatus('Connecting to server...')
      await delegateRun()
      await waitForDelegation(pubkey)
      this.showStatus('Generating seed...')
      await requestVrfSeed()
      await this.sleep(500)

      // Fetch RunState with seed
      const runState = await waitForSeed(pubkey)
      if (!runState) throw new Error('RunState not found after start')
      cacheRunState(runState)

      // Re-fetch player state (total_runs updated)
      const ps = await fetchPlayerState(pubkey)
      if (ps) cachePlayerState(ps)

      // Pass seed to DrillScene
      const seedBytes = runState.seed.slice(0, 4)
      const baseSeed = new DataView(seedBytes.buffer, seedBytes.byteOffset, 4).getInt32(0, true)

      this.hideStatus()
      this.scene.start('DrillScene', { column, seed: baseSeed, runState })
    } catch (err) {
      console.error('Start drill failed:', err)
      await this.flashError('Start drill failed')
      this.busy = false
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}
